// Structure set definitions + per-chunk planning (STR-1..4/M6.3). PURE (ENG-1).
//
// Ties the region-grid planner (planner.ts) to the authored pieces (pieces.ts):
// each structure type is scattered on its own region grid, accepted by a biome +
// flatness predicate, and expanded into piece instances at gen time. The
// generator asks `planStructuresInChunk` which pieces touch a chunk (to stamp
// their blocks); the host asks `structureAnchorsInChunk` for the block entities
// (loot chests / spawners) those pieces place. `/locate` walks the same grid.
import { WORLD, STRUCTURE } from '../../config';
import { defineSalt } from '../../engine/rng';
import {
  type Schematic, type StampTarget, type StateResolver, type PlacedAnchor,
  stampSchematic, schematicBounds, rotateOffset,
} from '../schematic-place';
import {
  type StructurePlacement, type Located,
  regionOf, regionCandidate, locateStructure,
} from '../planner';
import { PIECES } from './pieces';

const S_VILLAGE = defineSalt('struct-village');
const S_PYRAMID = defineSalt('struct-pyramid');
const S_OUTPOST = defineSalt('struct-outpost');
const S_DUNGEON = defineSalt('struct-dungeon');
const S_DESERT_WELL = defineSalt('struct-desert-well');
const S_TRAIL_RUINS = defineSalt('struct-trail-ruins');
const S_BURIED_TREASURE = defineSalt('struct-buried-treasure');
const S_SHIPWRECK = defineSalt('struct-shipwreck');
const S_OCEAN_RUINS = defineSalt('struct-ocean-ruins');
const S_OCEAN_MONUMENT = defineSalt('struct-ocean-monument');
const S_ANCIENT_CITY = defineSalt('struct-ancient-city');
const S_NETHER_FORTRESS = defineSalt('struct-nether-fortress');
const S_BASTION = defineSalt('struct-bastion-remnant');
const S_END_CITY = defineSalt('struct-end-city');
const S_STRONGHOLD = defineSalt('struct-stronghold');
const S_END_SHIP = defineSalt('struct-end-ship');
const S_END_TOWER = defineSalt('struct-end-city-tower');
const S_BASTION_FAMILY = defineSalt('struct-bastion-family');
const S_OCEAN_RUINS_FAMILY = defineSalt('struct-ocean-ruins-family');
const S_VILLAGE_LAYOUT = defineSalt('struct-village-layout');

/** Terrain/biome sampling the planner needs (supplied by the generator). */
export interface StructureEnv {
  terrainHeight(x: number, z: number): number;
  biomeName(x: number, z: number): string;
  /** Which dimension is being planned; absent ⇒ overworld (every caller before
   *  nether structures, so their behaviour is unchanged). */
  dimension?: StructureDimension;
}

export type StructureDimension = 'overworld' | 'nether' | 'end';

/** A concrete piece placed in the world, ready to stamp. */
export interface PlacedPiece {
  schem: Schematic;
  ox: number;
  oy: number;
  oz: number;
  rot: number;
  ground: 'raze' | 'bury';
  /** Schematic layer index that rests at ground level (raze foundation). */
  groundLayer: number;
}

interface StructureDef {
  type: string;
  /** Dimension this structure belongs to (default `'overworld'`). */
  dimension?: StructureDimension;
  placement: StructurePlacement;
  /** Accept only in these surface biomes (null = any land). */
  biomes: ReadonlySet<string> | null;
  /** Where the structure may sit relative to sea level (default `'land'`):
   *  `'land'` needs a surface at or above sea level (villages/dungeons);
   *  `'water'` needs a seafloor at least SUBMERGED_MIN_DEPTH below it (ocean ruins);
   *  `'any'` ignores the surface entirely — for deep structures buried far below it
   *  (the ancient city), which are just as happy under an ocean as under a hill. */
  medium?: 'land' | 'water' | 'any';
  /** Reject steep candidates (villages/pyramids). */
  needsFlat: boolean;
  /** Flatness tolerance when `needsFlat` (default STRUCTURE.FLATNESS_MAX_RANGE).
   *  The nether floor is far rougher than overworld terrain, so its structures —
   *  which bury and carve their own halls anyway — take a looser bound; the strict
   *  overworld default would make them vanishingly rare. */
  flatMaxRange?: number;
  /** Chebyshev chunk reach of the footprint (for the intersection scan). */
  reachChunks: number;
  /** Footprint used by the flatness predicate, in blocks. */
  flatSpan: number;
  build(seed: number | string | bigint, occx: number, occz: number, env: StructureEnv): PlacedPiece[];
}

/** Structure origin = its region's candidate chunk NW corner (block coords). */
function originBlock(occx: number, occz: number): [number, number] {
  return [occx * WORLD.SECTION, occz * WORLD.SECTION];
}

// ── Flatness predicate (STR-1) ───────────────────────────────────────────────
/** Max−min surface height over an N×N grid on the footprint; true if ≤ maxRange. */
export function isFlatEnough(
  terrainHeight: (x: number, z: number) => number,
  x0: number,
  z0: number,
  span: number,
  maxRange: number = STRUCTURE.FLATNESS_MAX_RANGE,
): boolean {
  let min = Infinity, max = -Infinity;
  const step = Math.max(1, Math.floor(span / 4));
  for (let dz = 0; dz <= span; dz += step) {
    for (let dx = 0; dx <= span; dx += step) {
      const h = terrainHeight(x0 + dx, z0 + dz);
      if (h < min) min = h;
      if (h > max) max = h;
    }
  }
  return max - min <= maxRange;
}

// ── Structure builders ───────────────────────────────────────────────────────
/** A deterministic 0..n−1 pick from a per-position hash (avoids constructing an
 *  Rng per piece in the hot scan). Used for anything a site decides for itself:
 *  which way a piece faces, which authored family it belongs to, whether a city
 *  moored a ship. */
function pickAt(seed: number | string | bigint, salt: string, x: number, z: number, n: number): number {
  let h = 2166136261 >>> 0;
  for (const c of `${String(seed)}#${salt}#${x}#${z}`) h = Math.imul(h ^ c.charCodeAt(0), 16777619) >>> 0;
  return (h >>> 3) % n;
}

/** A small deterministic rotation 0..3 from a per-position hash. */
function rotAt(seed: number | string | bigint, salt: string, x: number, z: number): number {
  return pickAt(seed, salt, x, z, 4);
}

function buildVillage(seed: number | string | bigint, occx: number, occz: number, env: StructureEnv): PlacedPiece[] {
  const [ox0, oz0] = originBlock(occx, occz);
  const groundY = env.terrainHeight(ox0 + 8, oz0 + 8);
  const at = (dx: number, dz: number, name: string, rot: number): PlacedPiece => ({
    schem: PIECES[name]!, ox: ox0 + dx, oy: groundY, oz: oz0 + dz, rot, ground: 'raze', groundLayer: 0,
  });
  const r = (dx: number, dz: number): number => rotAt(seed, S_VILLAGE_LAYOUT, ox0 + dx, oz0 + dz);
  return [
    at(6, 6, 'village/well', 0),
    at(0, 0, 'village/house_small', r(0, 0)),
    at(12, 1, 'village/house_small', r(12, 1)),
    at(1, 12, 'village/house_small', r(1, 12)),
    at(6, 1, 'village/lamp_post', 0),
    at(6, 13, 'village/lamp_post', 0),
  ];
}

function buildPyramid(seed: number | string | bigint, occx: number, occz: number, env: StructureEnv): PlacedPiece[] {
  const [ox0, oz0] = originBlock(occx, occz);
  const groundY = env.terrainHeight(ox0 + 4, oz0 + 4);
  const GROUND_LAYER = 3; // layers 0..2 are the buried room; layer 3 is the base
  return [{
    schem: PIECES['desert_pyramid']!, ox: ox0 + 3, oy: groundY - GROUND_LAYER, oz: oz0 + 3,
    rot: 0, ground: 'raze', groundLayer: GROUND_LAYER,
  }];
}

function buildOutpost(seed: number | string | bigint, occx: number, occz: number, env: StructureEnv): PlacedPiece[] {
  const [ox0, oz0] = originBlock(occx, occz);
  const groundY = env.terrainHeight(ox0 + 2, oz0 + 2); // 5×5 footprint centre
  return [{
    schem: PIECES['pillager_outpost']!, ox: ox0, oy: groundY, oz: oz0,
    rot: rotAt(seed, S_OUTPOST, ox0, oz0), ground: 'raze', groundLayer: 0,
  }];
}

function buildDungeon(seed: number | string | bigint, occx: number, occz: number, env: StructureEnv): PlacedPiece[] {
  const [ox0, oz0] = originBlock(occx, occz);
  const surfaceY = env.terrainHeight(ox0 + 4, oz0 + 4);
  return [{
    schem: PIECES['dungeon']!, ox: ox0 + 4, oy: surfaceY - STRUCTURE.DUNGEON_DEPTH, oz: oz0 + 4,
    rot: rotAt(seed, S_DUNGEON, ox0, oz0), ground: 'bury', groundLayer: 0,
  }];
}

/** A desert well: a small sandstone well on the surface, buried suspicious sand at
 *  the bottom. Layer 1 (the sandstone ring) sits at ground level. */
function buildDesertWell(seed: number | string | bigint, occx: number, occz: number, env: StructureEnv): PlacedPiece[] {
  const [ox0, oz0] = originBlock(occx, occz);
  const groundY = env.terrainHeight(ox0 + 1, oz0 + 1); // the well centre
  return [{
    schem: PIECES['desert_well']!, ox: ox0, oy: groundY - 1, oz: oz0,
    rot: rotAt(seed, S_DESERT_WELL, ox0, oz0), ground: 'raze', groundLayer: 1,
  }];
}

/** Trail ruins: a small buried stone-brick chamber (suspicious-gravel floor + a
 *  relic chest), sitting a few blocks under the surface like a dungeon. */
function buildTrailRuins(seed: number | string | bigint, occx: number, occz: number, env: StructureEnv): PlacedPiece[] {
  const [ox0, oz0] = originBlock(occx, occz);
  const surfaceY = env.terrainHeight(ox0 + 2, oz0 + 2);
  return [{
    schem: PIECES['trail_ruins']!, ox: ox0, oy: surfaceY - 4, oz: oz0,
    rot: rotAt(seed, S_TRAIL_RUINS, ox0, oz0), ground: 'bury', groundLayer: 0,
  }];
}

/** Buried treasure: a single loot chest buried deep in the beach sand (a treasure
 *  map leads here). */
function buildBuriedTreasure(seed: number | string | bigint, occx: number, occz: number, env: StructureEnv): PlacedPiece[] {
  const [ox0, oz0] = originBlock(occx, occz);
  const surfaceY = env.terrainHeight(ox0, oz0);
  return [{
    schem: PIECES['buried_treasure']!, ox: ox0, oy: surfaceY - 5, oz: oz0,
    rot: 0, ground: 'bury', groundLayer: 0,
  }];
}

/** Shipwreck: a half-buried spruce hull with treasure/supply/map chests. Layer 1
 *  (the hull floor) rests at the surface; the keel below buries. Two variants off
 *  one piece: beached wrecks raze their footprint flat like any land structure,
 *  while a sunken one buries instead — razing underwater would blow an air bubble
 *  through the ocean above the deck, so the seabed is left as it lies. */
function buildShipwreck(seed: number | string | bigint, occx: number, occz: number, env: StructureEnv): PlacedPiece[] {
  const [ox0, oz0] = originBlock(occx, occz);
  const groundY = env.terrainHeight(ox0 + 2, oz0 + 3); // hull centre (5×7 footprint)
  const sunken = groundY < WORLD.SEA_LEVEL;
  return [{
    schem: PIECES['shipwreck']!, ox: ox0, oy: groundY - 1, oz: oz0,
    rot: rotAt(seed, S_SHIPWRECK, ox0, oz0), ground: sunken ? 'bury' : 'raze', groundLayer: 1,
  }];
}

/** Ocean ruins: a broken platform resting on the seafloor, its rooms left
 *  water-filled ('.' keeps the surrounding water) with a relic chest inside.
 *
 *  Two authored families off one def — the cold mossy stone-brick ring and the warm
 *  sandstone one — because shipping only the cold piece made every ocean in the
 *  world look identical. Minecraft splits the families by warm/cold OCEAN biome;
 *  this world's climate has a single `ocean`, so the family is a deterministic
 *  per-site pick instead. Both are 7×7, so the region grid and the flatness span
 *  are shared and only the material changes. */
function buildOceanRuins(seed: number | string | bigint, occx: number, occz: number, env: StructureEnv): PlacedPiece[] {
  const [ox0, oz0] = originBlock(occx, occz);
  const floorY = env.terrainHeight(ox0 + 3, oz0 + 3); // 7×7 footprint centre
  const warm = pickAt(seed, S_OCEAN_RUINS_FAMILY, ox0, oz0, 2) === 0;
  return [{
    schem: PIECES[warm ? 'ocean_ruins_warm' : 'ocean_ruins']!, ox: ox0, oy: floorY - 1, oz: oz0,
    rot: rotAt(seed, S_OCEAN_RUINS, ox0, oz0), ground: 'bury', groundLayer: 0,
  }];
}

/** Ocean monument: the deep-ocean prismarine temple. Its base slab replaces the
 *  seafloor, so the shell rises through the water column above it. */
function buildOceanMonument(seed: number | string | bigint, occx: number, occz: number, env: StructureEnv): PlacedPiece[] {
  const [ox0, oz0] = originBlock(occx, occz);
  const floorY = env.terrainHeight(ox0 + 6, oz0 + 6); // 13×13 footprint centre
  return [{
    schem: PIECES['ocean_monument']!, ox: ox0, oy: floorY, oz: oz0,
    rot: rotAt(seed, S_OCEAN_MONUMENT, ox0, oz0), ground: 'bury', groundLayer: 0,
  }];
}

/**
 * Stronghold (DIM-3): the way into The End, and the piece the world was missing
 * outright — the dragon fight, the gateways, the end cities and the elytra all
 * existed with no door to any of them.
 *
 * Four pieces make a complex rather than a lone room: the portal room at the
 * origin, a corridor east to the library and another west to the storeroom. The
 * corridors are returned LAST on purpose — pieces stamp in list order, and a
 * corridor's open end column is what carves the doorway through the room wall it
 * meets. Stamp them first and the rooms would seal themselves back up.
 *
 * Like the ancient city it sits at a fixed Y in solid rock and carves its own
 * halls, so it works under a hill, a desert or an ocean without a flatness test.
 */
function buildStronghold(seed: number | string | bigint, occx: number, occz: number): PlacedPiece[] {
  const [ox0, oz0] = originBlock(occx, occz);
  const y = STRUCTURE.STRONGHOLD_Y;
  const rot = rotAt(seed, S_STRONGHOLD, ox0, oz0);
  const at = (name: string, dx: number, dz: number): PlacedPiece => {
    const [rx, rz] = rotateOffset(dx, dz, rot);
    return { schem: PIECES[name]!, ox: ox0 + rx, oy: y, oz: oz0 + rz, rot, ground: 'bury', groundLayer: 0 };
  };
  return [
    at('stronghold/portal_room', 0, 0),
    at('stronghold/library', STRUCTURE.STRONGHOLD_LIBRARY_DX, STRUCTURE.STRONGHOLD_LIBRARY_DZ),
    at('stronghold/storeroom', STRUCTURE.STRONGHOLD_STOREROOM_DX, STRUCTURE.STRONGHOLD_STOREROOM_DZ),
    at('stronghold/corridor', STRUCTURE.STRONGHOLD_CORRIDOR_DX, STRUCTURE.STRONGHOLD_CORRIDOR_DZ),
    at('stronghold/corridor', STRUCTURE.STRONGHOLD_WEST_CORRIDOR_DX, STRUCTURE.STRONGHOLD_CORRIDOR_DZ),
  ];
}

/** Ancient city: the deep-dark ruin. Unlike every other structure it ignores the
 *  surface entirely and stamps at a fixed deep Y in solid deepslate, carving its own
 *  halls — so it sits under hills and oceans alike. */
function buildAncientCity(seed: number | string | bigint, occx: number, occz: number): PlacedPiece[] {
  const [ox0, oz0] = originBlock(occx, occz);
  return [{
    schem: PIECES['ancient_city']!, ox: ox0, oy: STRUCTURE.ANCIENT_CITY_Y, oz: oz0,
    rot: rotAt(seed, S_ANCIENT_CITY, ox0, oz0), ground: 'bury', groundLayer: 0,
  }];
}

/** Nether fortress: the nether-brick keep, planted on the netherrack floor of the
 *  wastes. `terrainHeight` in the nether is the floor top + 1, so the base course
 *  sits one below it and the keep rises out of the rock. */
function buildNetherFortress(seed: number | string | bigint, occx: number, occz: number, env: StructureEnv): PlacedPiece[] {
  const [ox0, oz0] = originBlock(occx, occz);
  const floorY = env.terrainHeight(ox0 + 5, oz0 + 4); // 11×9 footprint centre
  return [{
    schem: PIECES['nether_fortress']!, ox: ox0, oy: floorY - 1, oz: oz0,
    rot: rotAt(seed, S_NETHER_FORTRESS, ox0, oz0), ground: 'bury', groundLayer: 0,
  }];
}

/** Bastion remnant: the piglin stronghold, a blackstone hulk on the nether floor.
 *
 *  Two layouts off one def — the walled KEEP and the open-roofed hoglin STABLE —
 *  because a nether where every bastion is the same building is a nether you stop
 *  looking at. Both are 13×13, so the region grid, the flatness span and the reach
 *  are shared and only the plan changes; this is the same per-site family pick the
 *  ocean ruins make. */
function buildBastion(seed: number | string | bigint, occx: number, occz: number, env: StructureEnv): PlacedPiece[] {
  const [ox0, oz0] = originBlock(occx, occz);
  const floorY = env.terrainHeight(ox0 + 6, oz0 + 6); // 13×13 footprint centre
  const stables = pickAt(seed, S_BASTION_FAMILY, ox0, oz0, STRUCTURE.BASTION_STABLES_ONE_IN) === 0;
  return [{
    schem: PIECES[stables ? 'bastion_stables' : 'bastion_remnant']!, ox: ox0, oy: floorY - 1, oz: oz0,
    rot: rotAt(seed, S_BASTION, ox0, oz0), ground: 'bury', groundLayer: 0,
  }];
}

/** End city: a purpur tower on an outer End island, plus the pieces a real city is
 *  actually made of — a short side ANNEX with its own shulker and chest, and (on
 *  some sites) the moored end SHIP whose hold carries the elytra.
 *
 *  The End has no sea level, so `medium:'any'` skips that gate entirely; the
 *  `end_highlands` biome is what keeps a city off the central arena and out of the
 *  void, since a void column reports the same fallback surface height as the flat
 *  arena and the flatness test alone can't tell them apart.
 *
 *  The ship FLOATS: it is stamped at a fixed height above the tower's footing with
 *  no ground layer at all, because a hull that razed or buried itself into the
 *  island would be a purpur shed rather than a ship. */
function buildEndCity(seed: number | string | bigint, occx: number, occz: number, env: StructureEnv): PlacedPiece[] {
  const [ox0, oz0] = originBlock(occx, occz);
  const groundY = env.terrainHeight(ox0 + 4, oz0 + 4); // 9x9 footprint centre
  const rot = rotAt(seed, S_END_CITY, ox0, oz0);
  // Every wing is offset in the TOWER's own rotated frame. A piece rotates about
  // its origin corner (STR-3), so a wing placed at a world-space offset drifts off
  // a rotated keep — which is exactly what the annex used to do, standing ten
  // blocks clear of a city turned 90°. Rotating the offsets makes the city one
  // rigid assembly, which is also what lets a bridge actually meet both towers.
  const at = (name: string, dx: number, dz: number, dy = 0): PlacedPiece => {
    const [rx, rz] = rotateOffset(dx, dz, rot);
    return { schem: PIECES[name]!, ox: ox0 + rx, oy: groundY + dy, oz: oz0 + rz, rot, ground: 'bury', groundLayer: 0 };
  };
  const out: PlacedPiece[] = [
    { schem: PIECES['end_city']!, ox: ox0, oy: groundY, oz: oz0, rot, ground: 'bury', groundLayer: 0 },
    at('end_city/annex', STRUCTURE.END_CITY_ANNEX_DX, STRUCTURE.END_CITY_ANNEX_DZ),
  ];
  if (pickAt(seed, S_END_SHIP, ox0, oz0, STRUCTURE.END_SHIP_ONE_IN) === 0) {
    out.push(at('end_city/ship', STRUCTURE.END_SHIP_DX, STRUCTURE.END_SHIP_DZ, STRUCTURE.END_SHIP_DY));
  }
  // A second, slimmer tower north of the keep, and the bridge that reaches it.
  // The bridge goes LAST for the same reason the stronghold's corridors do: its
  // end rows carve the doorways through the two towers' walls, and a tower
  // stamped after it would wall the walkway straight back up.
  if (pickAt(seed, S_END_TOWER, ox0, oz0, STRUCTURE.END_CITY_TOWER_ONE_IN) === 0) {
    out.push(at('end_city/tower', STRUCTURE.END_CITY_TOWER_DX, STRUCTURE.END_CITY_TOWER_DZ));
    out.push(at('end_city/bridge', STRUCTURE.END_CITY_BRIDGE_DX, STRUCTURE.END_CITY_BRIDGE_DZ));
  }
  return out;
}

export const STRUCTURE_DEFS: readonly StructureDef[] = [
  {
    type: 'end_city',
    dimension: 'end',
    placement: { salt: S_END_CITY, ...STRUCTURE.END_CITY },
    biomes: new Set(['end_highlands']),
    medium: 'any',
    needsFlat: true,
    flatMaxRange: STRUCTURE.END_FLATNESS_MAX_RANGE,
    // The city is no longer one 9-wide tower: its annex reaches 17 blocks east, its
    // ship 7 west and its second tower 20 north, so the intersection scan needs
    // three chunks of reach or a chunk on the far side of a wing generates without it.
    reachChunks: 3,
    flatSpan: 9,
    build: buildEndCity,
  },
  {
    type: 'nether_fortress',
    dimension: 'nether',
    placement: { salt: S_NETHER_FORTRESS, ...STRUCTURE.NETHER_FORTRESS },
    // The wastes and the soul sand valley are open enough to hold a keep; the
    // fungal forests and deltas are too broken up.
    biomes: new Set(['nether_wastes', 'soul_sand_valley']),
    medium: 'any',
    needsFlat: true,
    flatMaxRange: STRUCTURE.NETHER_FLATNESS_MAX_RANGE,
    reachChunks: 1,
    flatSpan: 11,
    build: buildNetherFortress,
  },
  {
    type: 'bastion_remnant',
    dimension: 'nether',
    placement: { salt: S_BASTION, ...STRUCTURE.BASTION_REMNANT },
    biomes: new Set(['nether_wastes', 'crimson_forest', 'warped_forest', 'basalt_deltas']),
    medium: 'any',
    needsFlat: true,
    flatMaxRange: STRUCTURE.NETHER_FLATNESS_MAX_RANGE,
    reachChunks: 2,
    flatSpan: 13,
    build: buildBastion,
  },
  {
    type: 'stronghold',
    placement: { salt: S_STRONGHOLD, ...STRUCTURE.STRONGHOLD },
    biomes: null,
    medium: 'any',
    needsFlat: false,
    // The complex reaches 20 blocks west and 32 east of its origin chunk, so the
    // intersection scan needs three chunks of reach or a wing generates alone.
    reachChunks: 3,
    flatSpan: 11,
    build: buildStronghold,
  },
  {
    type: 'ancient_city',
    placement: { salt: S_ANCIENT_CITY, ...STRUCTURE.ANCIENT_CITY },
    biomes: null,
    medium: 'any',
    needsFlat: false,
    reachChunks: 2,
    flatSpan: 15,
    build: buildAncientCity,
  },
  {
    type: 'ocean_monument',
    placement: { salt: S_OCEAN_MONUMENT, ...STRUCTURE.OCEAN_MONUMENT },
    biomes: new Set(['deep_ocean']),
    medium: 'water',
    // The temple is 6 tall; a flat-ish seabed keeps it from bursting out of a slope.
    needsFlat: true,
    reachChunks: 2,
    flatSpan: 13,
    build: buildOceanMonument,
  },
  {
    type: 'ocean_ruins',
    placement: { salt: S_OCEAN_RUINS, ...STRUCTURE.OCEAN_RUINS },
    biomes: new Set(['ocean', 'deep_ocean']),
    medium: 'water',
    needsFlat: false,
    reachChunks: 1,
    flatSpan: 7,
    build: buildOceanRuins,
  },
  {
    type: 'buried_treasure',
    placement: { salt: S_BURIED_TREASURE, ...STRUCTURE.BURIED_TREASURE },
    biomes: new Set(['beach']),
    needsFlat: false,
    reachChunks: 1,
    flatSpan: 1,
    build: buildBuriedTreasure,
  },
  {
    type: 'shipwreck',
    placement: { salt: S_SHIPWRECK, ...STRUCTURE.SHIPWRECK },
    // Beached *and* sunken: 'any' skips the sea-level gate so a wreck can settle on
    // the seabed too; `buildShipwreck` picks raze/bury from the depth it lands at.
    biomes: new Set(['beach', 'ocean', 'deep_ocean']),
    medium: 'any',
    needsFlat: false,
    reachChunks: 1,
    flatSpan: 7,
    build: buildShipwreck,
  },
  {
    type: 'trail_ruins',
    placement: { salt: S_TRAIL_RUINS, ...STRUCTURE.TRAIL_RUINS },
    biomes: null,
    needsFlat: false,
    reachChunks: 1,
    flatSpan: 5,
    build: buildTrailRuins,
  },
  {
    type: 'desert_well',
    placement: { salt: S_DESERT_WELL, ...STRUCTURE.DESERT_WELL },
    biomes: new Set(['desert']),
    needsFlat: true,
    reachChunks: 1,
    flatSpan: 3,
    build: buildDesertWell,
  },
  {
    type: 'village',
    placement: { salt: S_VILLAGE, ...STRUCTURE.VILLAGE },
    biomes: new Set(['plains', 'forest']),
    needsFlat: true,
    reachChunks: 2,
    flatSpan: 16,
    build: buildVillage,
  },
  {
    type: 'desert_pyramid',
    placement: { salt: S_PYRAMID, ...STRUCTURE.DESERT_PYRAMID },
    biomes: new Set(['desert']),
    needsFlat: true,
    reachChunks: 1,
    flatSpan: 10,
    build: buildPyramid,
  },
  {
    type: 'pillager_outpost',
    placement: { salt: S_OUTPOST, ...STRUCTURE.PILLAGER_OUTPOST },
    biomes: new Set(['plains', 'forest', 'desert']),
    needsFlat: true,
    reachChunks: 1,
    flatSpan: 5,
    build: buildOutpost,
  },
  {
    type: 'dungeon',
    placement: { salt: S_DUNGEON, ...STRUCTURE.DUNGEON },
    biomes: null,
    needsFlat: false,
    reachChunks: 1,
    flatSpan: 8,
    build: buildDungeon,
  },
];

const DEF_BY_TYPE = new Map(STRUCTURE_DEFS.map((d) => [d.type, d]));

/** The structure set for one dimension (a nether chunk never plans a village). */
function defsFor(env: StructureEnv): readonly StructureDef[] {
  const dim = env.dimension ?? 'overworld';
  return STRUCTURE_DEFS.filter((d) => (d.dimension ?? 'overworld') === dim);
}

/** Does a candidate at region-chunk (occx,occz) become a real structure? */
export function acceptStructure(def: StructureDef, occx: number, occz: number, env: StructureEnv): boolean {
  const [ox0, oz0] = originBlock(occx, occz);
  const cx = ox0 + Math.floor(def.flatSpan / 2), cz = oz0 + Math.floor(def.flatSpan / 2);
  if (def.biomes && !def.biomes.has(env.biomeName(cx, cz))) return false;
  const surface = env.terrainHeight(cx, cz);
  if (def.medium === 'water') {
    // Submerged: the seafloor must lie well under the waves, so the piece is
    // actually underwater rather than poking out of a shallow.
    if (surface > WORLD.SEA_LEVEL - STRUCTURE.SUBMERGED_MIN_DEPTH) return false;
  } else if (def.medium !== 'any' && surface < WORLD.SEA_LEVEL) {
    return false; // land only (no drowned dungeons / sunken villages)
  }
  if (def.needsFlat && !isFlatEnough(env.terrainHeight, ox0, oz0, def.flatSpan, def.flatMaxRange)) return false;
  return true;
}

function chunkOverlaps(b: { minX: number; maxX: number; minZ: number; maxZ: number }, cx: number, cz: number): boolean {
  const x0 = cx * WORLD.SECTION, x1 = x0 + WORLD.SECTION - 1;
  const z0 = cz * WORLD.SECTION, z1 = z0 + WORLD.SECTION - 1;
  return b.minX <= x1 && b.maxX >= x0 && b.minZ <= z1 && b.maxZ >= z0;
}

/** Every piece whose footprint overlaps chunk (cx,cz), across all structure types. */
export function planStructuresInChunk(
  seed: number | string | bigint,
  cx: number,
  cz: number,
  env: StructureEnv,
): PlacedPiece[] {
  const out: PlacedPiece[] = [];
  for (const def of defsFor(env)) {
    const reach = def.reachChunks;
    const [rMinX, rMinZ] = regionOf(def.placement, cx - reach, cz - reach);
    const [rMaxX, rMaxZ] = regionOf(def.placement, cx + reach, cz + reach);
    for (let rz = rMinZ; rz <= rMaxZ; rz++) {
      for (let rx = rMinX; rx <= rMaxX; rx++) {
        const [occx, occz] = regionCandidate(seed, def.placement, rx, rz);
        if (!acceptStructure(def, occx, occz, env)) continue;
        for (const piece of def.build(seed, occx, occz, env)) {
          const b = schematicBounds(piece.schem, [piece.ox, piece.oy, piece.oz], piece.rot);
          if (chunkOverlaps(b, cx, cz)) out.push(piece);
        }
      }
    }
  }
  return out;
}

/** The block-entity anchors (loot chests / spawners) landing in chunk (cx,cz). */
export function structureAnchorsInChunk(
  seed: number | string | bigint,
  cx: number,
  cz: number,
  env: StructureEnv,
  res: StateResolver,
): PlacedAnchor[] {
  const out: PlacedAnchor[] = [];
  const x0 = cx * WORLD.SECTION, x1 = x0 + WORLD.SECTION - 1;
  const z0 = cz * WORLD.SECTION, z1 = z0 + WORLD.SECTION - 1;
  const target: StampTarget = {
    set: () => {}, // anchors only — blocks are stamped by the generator
    anchor: (a) => { if (a.x >= x0 && a.x <= x1 && a.z >= z0 && a.z <= z1) out.push(a); },
  };
  for (const piece of planStructuresInChunk(seed, cx, cz, env)) {
    stampSchematic(piece.schem, [piece.ox, piece.oy, piece.oz], piece.rot, res, target);
  }
  return out;
}

/**
 * Is (x,z) standing inside a structure of `type`? Walks the same region grid as
 * `planStructuresInChunk`, but tests the column against each piece's footprint
 * rather than a chunk's — what a structure-gated mob spawn needs to ask (an elder
 * guardian belongs to its monument, not to the open ocean).
 */
export function insideStructure(
  type: string,
  seed: number | string | bigint,
  x: number,
  z: number,
  env: StructureEnv,
): boolean {
  const def = DEF_BY_TYPE.get(type);
  if (!def || (def.dimension ?? 'overworld') !== (env.dimension ?? 'overworld')) return false;
  const cx = Math.floor(x / WORLD.SECTION), cz = Math.floor(z / WORLD.SECTION);
  const reach = def.reachChunks;
  const [rMinX, rMinZ] = regionOf(def.placement, cx - reach, cz - reach);
  const [rMaxX, rMaxZ] = regionOf(def.placement, cx + reach, cz + reach);
  for (let rz = rMinZ; rz <= rMaxZ; rz++) {
    for (let rx = rMinX; rx <= rMaxX; rx++) {
      const [occx, occz] = regionCandidate(seed, def.placement, rx, rz);
      if (!acceptStructure(def, occx, occz, env)) continue;
      for (const piece of def.build(seed, occx, occz, env)) {
        const b = schematicBounds(piece.schem, [piece.ox, piece.oy, piece.oz], piece.rot);
        if (x >= b.minX && x <= b.maxX && z >= b.minZ && z <= b.maxZ) return true;
      }
    }
  }
  return false;
}

export interface LocatedStructure extends Located {
  type: string;
}

/** Nearest structure of `type` to a from-chunk (STR-4), or null. */
export function locateStructureType(
  type: string,
  seed: number | string | bigint,
  fromCx: number,
  fromCz: number,
  env: StructureEnv,
  maxRings = 128,
): LocatedStructure | null {
  const def = DEF_BY_TYPE.get(type);
  if (!def || (def.dimension ?? 'overworld') !== (env.dimension ?? 'overworld')) return null;
  const found = locateStructure(seed, def.placement, fromCx, fromCz, (occx, occz) => acceptStructure(def, occx, occz, env), maxRings);
  return found ? { ...found, type } : null;
}

/** Structure type names `/locate` accepts (optionally for one dimension). */
export function structureTypeNames(dimension?: StructureDimension): string[] {
  const defs = dimension ? STRUCTURE_DEFS.filter((d) => (d.dimension ?? 'overworld') === dimension) : STRUCTURE_DEFS;
  return defs.map((d) => d.type);
}
